import { Fragment, useEffect } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SendIcon from "@mui/icons-material/Send";
import PrepaymentFormData from "~/store/PrepaymentFormData.js";
import { formatCpf, onlyDigits } from "~/features/doctorForm/masks";
import DutyLine from "~/features/doctorForm/DutyLine";
import { FormTitle, SectionLabel } from "~/features/doctorForm/FormLayout";
import { formatBRL } from "~/shared/ui/format";

const Head = ({ title, onEdit }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
    <SectionLabel>{title}</SectionLabel>
    <Tooltip title="Editar">
      <IconButton aria-label={`Editar ${title.replace(/:$/, "")}`} onClick={onEdit} color="primary">
        <EditOutlinedIcon />
      </IconButton>
    </Tooltip>
  </Box>
);

const Step5 = ({ setActiveStep, setDisabledNext, mobile }) => {
  const d = PrepaymentFormData.data;
  const total = d.vacancies.reduce((sum, duty) => sum + (Number(duty.value) || 0), 0);
  const empty = d.vacancies.length === 0;
  // Só libera o envio com plantões (no fluxo antigo o passo 4 nunca deixava chegar aqui sem nenhum).
  useEffect(() => {
    setDisabledNext?.(empty);
  }, [empty, setDisabledNext]);
  // onlyDigits antes de formatar: um CPF que chegue já pontuado não vira "529..9".
  const rows = [
    ["Nome", d.fullName],
    ["E-mail", d.email],
    ["CPF", formatCpf(onlyDigits(d.cpf))],
    ["CRM", d.crm],
    ["CRMUF", d.crmuf],
  ];
  return (
    <Box>
      <FormTitle mobile={mobile} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        Revise os dados antes de enviar <SendIcon sx={{ color: "#05bbe5" }} aria-hidden />
      </FormTitle>
      <Paper variant="outlined" sx={{ mt: 3, p: mobile ? 2 : 2.5, borderRadius: 2 }}>
        <Head
          title="Dados Pessoais"
          onEdit={() => {
            // Como hoje: editar os dados limpa os plantões (a busca do passo 4 depende do CRM).
            PrepaymentFormData.setDutys([]);
            setActiveStep(2);
          }}
        />
        <Box
          component="dl"
          sx={{
            m: 0,
            display: "grid",
            gridTemplateColumns: mobile ? "minmax(0, 1fr)" : "120px minmax(0, 1fr)",
            columnGap: 2,
            rowGap: mobile ? 0 : 1,
            "& dt": { fontSize: 13, color: "text.secondary", fontWeight: 500, mt: mobile ? 1 : 0 },
            "& dd": { m: 0, fontSize: 15, fontWeight: 500, overflowWrap: "anywhere" },
          }}
        >
          {rows.map(([k, v]) => (
            <Fragment key={k}>
              <dt>{k}:</dt>
              <dd>{v}</dd>
            </Fragment>
          ))}
        </Box>
      </Paper>
      <Box sx={{ mt: 3 }}>
        <Head title="Plantões selecionados:" onEdit={() => setActiveStep(3)} />
      </Box>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        {d.vacancies.length === 0 ? (
          <Typography sx={{ textAlign: "center", py: 3, color: "text.secondary" }}>Nenhum Plantão selecionado.</Typography>
        ) : (
          d.vacancies.map((duty, i) => (
            <Box key={duty.id} sx={{ borderTop: i ? "1px solid #eceef1" : "none" }}>
              <DutyLine duty={duty} mobile={mobile} action={<CheckCircleIcon sx={{ color: "#2e7d32", m: 1 }} aria-hidden />} />
            </Box>
          ))
        )}
      </Paper>
      <Box sx={{ mt: 3, p: 2, bgcolor: "#f0f7fa", borderRadius: 2, border: "1px solid #d6ecf3" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Valor total:</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: "primary.main" }}>{formatBRL(total)}</Typography>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.5 }}>
          * É o valor total sem considerar taxas nem descontos provenientes da empresa medica.
        </Typography>
      </Box>
    </Box>
  );
};

export default Step5;
